import React, { useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { TopBottomCuttedCard } from "../../../common/components/top-bottom-cutted-card/TopBottomCuttedCard";
import { TransactionReceiptHeader, ShareReceiptModal } from "../history/customer-history-detail/components";
import { shareReceiptAsImage } from "../../../common/utils/receipt/shareReceiptAsImage";
import { shareReceiptAsPdf } from "../../../common/utils/receipt/shareReceiptAsPdf";
import { ShareIcon, VendingTransactionHistory } from "../../../assets/icons";
import bgTransaction from "../../../assets/background/bg-transaction.png";
import { useTenant } from "../../../tenant/branding/useTenant";
import Button from "../../../common/ekedc-components/button/Button";
// Reuse the history detail layout so the receipt matches Transaction History.
import styles from "../history/customer-history-detail/customer-transaction-history-detail.module.scss";
import { PaymentReceiptBody } from "./components/PaymentReceiptBody";
import { StatementOfAccountTransaction } from "../../../model/vending/vending-interfaces";
import { ConsumerType } from "../../../model/energy-profile";
import { useGetPrepaidTransactionByReferenceIdQuery } from "../../../service/vending-service";
import { transactionTypeLabel } from "./historical-payment.utils";

export const HistoricalPaymentDetailPage = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { reference } = useParams();
	const { state } = useLocation();
	// Statement row carried from the list (for header amount/date/type); detail fetched by referenceId.
	const transaction = (state as { transaction?: StatementOfAccountTransaction } | null)?.transaction;

	const [showShareModal, setShowShareModal] = useState(false);
	const receiptRef = useRef<HTMLDivElement>(null);
	const modalReceiptRef = useRef<HTMLDivElement>(null);
	const { logoWithText } = useTenant();

	const { data: detail, isFetching, error } = useGetPrepaidTransactionByReferenceIdQuery(reference ?? "", { skip: !reference });

	const referenceId = transaction?.referenceId ?? reference;
	const handleShareImage = () => {
		if (!modalReceiptRef.current || !referenceId) return;
		shareReceiptAsImage(modalReceiptRef.current, referenceId);
	};
	const handleSharePdf = () => {
		if (!modalReceiptRef.current || !referenceId) return;
		shareReceiptAsPdf(modalReceiptRef.current, referenceId);
	};

	if (isFetching) {
		return (
			<CustomerPageLayout title={t("history.web.paymentDetails")} onBack={() => navigate(-1)}>
				<div className={styles.pageContainer}>
					<p>{t("history.web.loadingPayments")}</p>
				</div>
			</CustomerPageLayout>
		);
	}

	if (error || !detail) {
		return (
			<CustomerPageLayout title={t("history.web.paymentDetails")} onBack={() => navigate(-1)}>
				<div className={styles.pageContainer}>
					<div className={styles.errorState}>
						<p>{t("history.web.paymentDetailsNotAvailable")}</p>
						<button className={styles.retryButton} onClick={() => navigate(-1)}>
							{t("history.web.goBack")}
						</button>
					</div>
				</div>
			</CustomerPageLayout>
		);
	}

	// Detail endpoint is prepaid-only, so the icon and label default to the prepaid (vend) treatment.
	const receiptIcon = <VendingTransactionHistory />;
	const typeLabel = transactionTypeLabel(transaction?.transactionType ?? "", ConsumerType.Prepaid, t);
	const amount = transaction?.amount?.amount ?? detail.transactionAmount ?? 0;
	const currency = transaction?.amount?.currency;
	const createdAt = transaction?.transactionDate ?? detail.createdDateTime ?? "";

	return (
		<CustomerPageLayout title={t("history.web.historicalPayment")} onBack={() => navigate(-1)} contentContainerClass={styles.contentDetails}>
			<div className={styles.pageContainer}>
				<TopBottomCuttedCard ref={receiptRef}>
					<TransactionReceiptHeader status={0} label={typeLabel} amount={amount} currency={currency} createdAt={createdAt} shareView={false} icon={receiptIcon} />
					<PaymentReceiptBody detail={detail} transaction={transaction} shareView={false} />
				</TopBottomCuttedCard>

				<Button variant="primary" className={styles.shareBtn} onClick={() => setShowShareModal(true)}>
					<ShareIcon /> {t("history.shareReceipt")}
				</Button>
			</div>

			{showShareModal && (
				<ShareReceiptModal
					ref={modalReceiptRef}
					onClose={() => setShowShareModal(false)}
					onShareImage={handleShareImage}
					onSharePdf={handleSharePdf}
					bgImage={bgTransaction}
					watermarkSrc={typeof logoWithText === "string" ? logoWithText : undefined}
					receiptStyle={{ "--receipt-circle-color": "var(--customer-bg)" } as React.CSSProperties}
				>
					<TransactionReceiptHeader status={0} label={typeLabel} amount={amount} currency={currency} createdAt={createdAt} shareView={true} icon={receiptIcon} />
					<PaymentReceiptBody detail={detail} transaction={transaction} shareView={true} />
				</ShareReceiptModal>
			)}
		</CustomerPageLayout>
	);
};
